import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthLayout, FieldError } from '@/components/auth/AuthLayout';
import { useUser } from '@/contexts/UserContext';
import { loginSchema, type LoginInput } from '@/lib/auth-schemas';

type LocationState = { from?: { pathname: string } };

export function Login() {
  const { login } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname ?? '/';
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    const result = await login(values.email, values.password);
    if (result.ok) {
      navigate(from, { replace: true });
      return;
    }
    if (result.reason === 'mfa-required') {
      navigate('/mfa/challenge', { state: { pendingToken: result.pendingToken } });
      return;
    }
    if (result.reason === 'mfa-setup-required') {
      navigate('/mfa/setup', { state: { pendingToken: result.pendingToken } });
      return;
    }
    if (result.reason === 'unconfirmed') {
      navigate('/confirm-email');
      return;
    }
    if (result.reason === 'use-sso') {
      toast.error('Esta conta usa SSO — clique em "Entrar com SSO".');
      return;
    }
    toast.error('Credenciais inválidas.');
  });

  return (
    <AuthLayout eyebrow="Acesso institucional">
      <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Entrar na plataforma</h2>
      <p style={{ color: 'var(--ev-fg-muted)', fontSize: 14, marginBottom: 28 }}>
        Use o e-mail cadastrado pela secretaria do seu órgão.
      </p>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 14 }}>
          <Label htmlFor="email" style={{ display: 'block', marginBottom: 6 }}>E-mail institucional</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email && <FieldError>{errors.email.message}</FieldError>}
        </div>

        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <Label htmlFor="password">Senha</Label>
            <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--ev-accent)', textDecoration: 'none' }}>
              Esqueci minha senha
            </Link>
          </div>
          <div style={{ position: 'relative' }}>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              style={{ paddingRight: 40 }}
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{ position: 'absolute', right: 6, top: 5, background: 'none', border: 0, padding: 6, cursor: 'pointer', color: 'var(--muted-foreground)' }}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <FieldError>{errors.password.message}</FieldError>}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted-foreground)', margin: '16px 0 24px', cursor: 'pointer' }}>
          <input type="checkbox" style={{ accentColor: 'var(--ev-accent)' }} />
          Manter sessão neste dispositivo
        </label>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          Entrar <ArrowRight size={16} />
        </Button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 18px' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--muted-foreground)', letterSpacing: '0.08em' }}>OU</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      <Button asChild variant="outline" className="w-full">
        <a
          href={`/api/sso/login?returnUrl=${encodeURIComponent(from)}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <Building2 size={16} /> Entrar com SSO
        </a>
      </Button>

      <p style={{ fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center', marginTop: 28 }}>
        Problemas para acessar? Contate{' '}
        <a href="mailto:banesprevsecretariageral@santander.com.br" style={{ color: 'var(--ev-accent)' }}>
          banesprevsecretariageral@santander.com.br
        </a>
      </p>
    </AuthLayout>
  );
}
